import React, { useState } from 'react';
import { apiUrl } from '../utils/api';
import './Contact.css';

interface ContactForm {
    name: string;
    email: string;
    message: string;
}

const Contact: React.FC = () => {
    const [formData, setFormData] = useState<ContactForm>({
        name: '',
        email: '',
        message: ''
    });
    const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
        type: null,
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (status.type === 'error') {
            setStatus({ type: null, message: '' });
        }
    };

    const validateForm = (): string | null => {
        if (formData.name.trim().length < 2) {
            return 'Please enter your full name (at least 2 characters).';
        }
        if (formData.name.trim().length > 100) {
            return 'Name is too long (maximum 100 characters).';
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return 'Please enter a valid email address.';
        }
        
        if (formData.message.trim().length < 10) {
            return 'Please provide a more detailed message (at least 10 characters).';
        }
        if (formData.message.trim().length > 2000) {
            return 'Message is too long (maximum 2000 characters).';
        }
        
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Client-side validation
        const validationError = validateForm();
        if (validationError) {
            setStatus({
                type: 'error',
                message: validationError
            });
            return;
        }

        setStatus({ type: null, message: '' });
        setIsSubmitting(true);

        try {
            const response = await fetch('/.netlify/functions/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send message');
            }

            setStatus({
                type: 'success',
                message: data.message || 'Thank you for your message. We will get back to you soon!'
            });
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            setStatus({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to send message. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-container">
            <h1>Contact Us</h1>
            <p className="contact-intro">
                Have questions or want to get involved? We'd love to hear from you.
                Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        disabled={isSubmitting}
                        minLength={2}
                        maxLength={100}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                        disabled={isSubmitting}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us how we can help..."
                        rows={5}
                        disabled={isSubmitting}
                        minLength={10}
                        maxLength={2000}
                    />
                    <small className="char-count">
                        {formData.message.length} / 2000 characters
                    </small>
                </div>

                <button type="submit" className="submit-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {status.message && (
                    <div className={`status-message ${status.type}`}>
                        {status.type === 'success' && '✓ '}
                        {status.type === 'error' && '✕ '}
                        {status.message}
                    </div>
                )}
            </form>

            <div className="contact-info">
                <h2>Other Ways to Reach Us</h2>
                <div className="contact-details">
                    <a href="mailto:info@letschangezambia.org" className="contact-item">
                        <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                        </svg>
                        <span><strong>Email:</strong> info@letschangezambia.org</span>
                    </a>
                    <a href="tel:+260955522188" className="contact-item">
                        <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                        </svg>
                        <span><strong>Phone:</strong> +260 95 552 2188 / +260 95 502 4018</span>
                    </a>
                    <div className="contact-item">
                        <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        <span><strong>Address:</strong> P.O. Box 310238, Lusaka, Zambia</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;